import { useState } from "react";

import type { Insight } from "../lib/api";
import { plural } from "../lib/format";
import { haptic } from "../lib/telegram";
import ExerciseIcon from "./ExerciseIcon";

interface Props {
  items: Insight[];
  onOpen: (exerciseId: number) => void;
}

/** Сколько строк видно сразу. Пять застоев подряд читались стеной текста. */
const VISIBLE = 3;

const GROUPS: { kind: Insight["kind"]; title: string; advice: string }[] = [
  {
    kind: "stagnation",
    title: "Застой",
    advice: "Застой обычно ломают сменой диапазона повторов или разгрузочной неделей.",
  },
  {
    kind: "abandoned",
    title: "Давно не делали",
    advice: "Если убрали намеренно — всё в порядке, это просто напоминание.",
  },
];

const kg = (v: number) => String(Number(v.toFixed(1)));

/** Короткий факт под названием: сколько раз и за какой срок. */
function fact(item: Insight): string {
  const times = plural(item.performances, "раз", "раза", "раз");
  return item.kind === "stagnation"
    ? `${times} за ${plural(item.days, "день", "дня", "дней")}`
    : `до этого ${times}`;
}

function Row({ item, onOpen }: { item: Insight; onOpen: () => void }) {
  const down = item.trend === "down";
  return (
    <button className="insight-row" onClick={onOpen}>
      <ExerciseIcon exercise={item.exercise} />
      <span className="insight-row__body">
        <span className="insight-row__name">{item.exercise.name_ru}</span>
        <span className="insight-row__fact">{fact(item)}</span>
      </span>
      {item.kind === "stagnation" && item.to_kg !== null ? (
        <span className="insight-row__metric">
          {kg(item.to_kg)} кг
          <small className={down ? "insight-row__sub--down" : ""}>
            {down && item.from_kg !== null ? `↓ с ${kg(item.from_kg)}` : "на месте"}
          </small>
        </span>
      ) : (
        <span className="insight-row__metric">
          {plural(item.days, "день", "дня", "дней")}
          <small>без него</small>
        </span>
      )}
    </button>
  );
}

/**
 * «На что посмотреть» — одна разметка на главную и профиль.
 *
 * Раньше каждая строка была абзацем прозы с красным «ЗАСТОЙ», и совет
 * повторялся у каждого упражнения слово в слово. Теперь: группа по виду,
 * у строки — факт и крупная цифра, совет — один раз под группой.
 */
export default function InsightList({ items, onOpen }: Props) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;

  const shown = expanded ? items : items.slice(0, VISIBLE);
  const hidden = items.length - shown.length;

  return (
    <div className="card insights">
      <p className="card__title">На что посмотреть</p>
      {GROUPS.map((group) => {
        const rows = shown.filter((i) => i.kind === group.kind);
        if (rows.length === 0) return null;
        const total = items.filter((i) => i.kind === group.kind).length;
        return (
          <section key={group.kind} className="insights__group">
            <div className="insights__head">
              <span className={`insights__tag insights__tag--${group.kind}`}>{group.title}</span>
              <span className="insights__count">{total}</span>
            </div>
            {rows.map((item) => (
              <Row
                key={`${item.kind}-${item.exercise.id}`}
                item={item}
                onOpen={() => {
                  haptic();
                  onOpen(item.exercise.id);
                }}
              />
            ))}
            <p className="insights__advice">{group.advice}</p>
          </section>
        );
      })}
      {items.length > VISIBLE && (
        <button
          className="insights__more"
          onClick={() => {
            haptic("light");
            setExpanded((v) => !v);
          }}
        >
          {expanded ? "Свернуть" : `Ещё ${plural(hidden, "упражнение", "упражнения", "упражнений")}`}
        </button>
      )}
    </div>
  );
}
